/**
 * Format/checksum validators for franchisee ABN, BSB, and bank account number.
 *
 * Duplicated by value from jims-mowing-api's src/utils/franchiseeFieldValidators.js
 * — this repo has no shared package with the API, so keep the two in sync by hand.
 *
 * Each validate* function returns `true` when valid, or a plain-English error
 * string when invalid, matching react-hook-form's `validate` rule shape so it
 * wires straight into FormField's `validation` prop.
 */

const ABN_FORMAT_REGEX = /^\d{11}$|^\d{2}\s?\d{3}\s?\d{3}\s?\d{3}$/;
const BSB_FORMAT_REGEX = /^\d{3}-?\d{3}$/;
const ACCOUNT_NUMBER_REGEX = /^\d{5,10}$/;

// ATO Modulus 89 weights, applied left to right after subtracting 1 from the first digit.
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

/** Strip spaces for storage/checksum (does not validate format). */
export const normalizeAbn = (abn) => (typeof abn === 'string' ? abn : '').replace(/\s/g, '');

/** Strip the hyphen for storage (does not validate format). */
export const normalizeBsb = (bsb) => (typeof bsb === 'string' ? bsb : '').replace(/-/g, '');

/** @param {string} elevenDigits - already format-checked, digits only */
const passesAbnChecksum = (elevenDigits) => {
  const digits = elevenDigits.split('').map(Number);
  digits[0] -= 1;
  const sum = digits.reduce((total, digit, i) => total + digit * ABN_WEIGHTS[i], 0);
  return sum % 89 === 0;
};

/**
 * @param {string} abn
 * @returns {true|string} true if valid, else a plain-English error message
 */
export const validateAbn = (abn) => {
  const value = typeof abn === 'string' ? abn.trim() : '';
  if (!ABN_FORMAT_REGEX.test(value)) {
    return 'Enter a valid 11-digit ABN';
  }
  if (!passesAbnChecksum(normalizeAbn(value))) {
    return 'Enter a valid 11-digit ABN';
  }
  return true;
};

/**
 * BSB is optional: an empty value passes so it can still be stored as null.
 * @param {string} bsb
 * @returns {true|string} true if valid, else a plain-English error message
 */
export const validateBsb = (bsb) => {
  if (bsb === null || bsb === undefined) return true;
  // A supplied non-string (e.g. a JSON number) is rejected rather than read as blank,
  // which would silently discard the bank details.
  if (typeof bsb !== 'string') return 'Enter a valid BSB, e.g. 062-000';
  const value = bsb.trim();
  if (!value) return true;
  if (!BSB_FORMAT_REGEX.test(value)) {
    return 'Enter a valid BSB, e.g. 062-000';
  }
  return true;
};

/**
 * Bank account number is optional: an empty value passes so it can still be
 * stored as null.
 * @param {string} accountNumber
 * @returns {true|string} true if valid, else a plain-English error message
 */
export const validateBankAccountNumber = (accountNumber) => {
  if (accountNumber === null || accountNumber === undefined) return true;
  // A supplied non-string (e.g. a JSON number) is rejected rather than read as blank,
  // which would silently discard the bank details.
  if (typeof accountNumber !== 'string') return 'Enter a valid bank account number (5-10 digits)';
  const value = accountNumber.trim();
  if (!value) return true;
  if (!ACCOUNT_NUMBER_REGEX.test(value)) {
    return 'Enter a valid bank account number (5-10 digits)';
  }
  return true;
};
